import {
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import { useTenantData } from "contexts/tenant";
import { useTenantTranslation } from "hooks/formatters";
import { Fragment } from "react";
import { MaterialRead, MixBasket } from "src/store/api/generatedApi";
import { DeltaMaterialRows } from "./DeltaMaterialRows";
import { MaterialDeltaRow } from "./materialDeltaRows";
import { useDeltaWeightFormat } from "./useDeltaWeightFormat";

interface Props {
  baskets: MixBasket[];
  adjustedBasketNumbers: Set<number>;
  rowsByBasketNumber: Map<number, MaterialDeltaRow[]>;
  onRowsChange: (args: {
    basketNumber: number;
    rows: MaterialDeltaRow[];
  }) => void;
  materials: MaterialRead[];
}

export const DeltaAdjustmentStep = ({
  baskets,
  adjustedBasketNumbers,
  rowsByBasketNumber,
  onRowsChange,
  materials,
}: Props) => {
  const { t } = useTenantTranslation();
  const { basket_layer_display_order: displayOrder } = useTenantData();
  const { formatWeight } = useDeltaWeightFormat();
  const shortNameByMaterialName = new Map(
    materials.map((material) => [material.name, material.short_name])
  );

  return (
    <Box
      sx={{
        display: "grid",
        columnGap: 3,
        rowGap: 2,
        gridTemplateColumns: {
          xs: "minmax(0, 1fr)",
          md: "fit-content(280px) minmax(0, 1fr)",
        },
        alignItems: "start",
      }}
    >
      {baskets.map((basket) => {
        const layers = [...basket.layers].sort((left, right) =>
          displayOrder === "top_first"
            ? right.layer_number - left.layer_number
            : left.layer_number - right.layer_number
        );
        return (
          <Fragment key={basket.basket_number}>
            <Box
              sx={{
                border: 1,
                borderColor: adjustedBasketNumbers.has(basket.basket_number)
                  ? "primary.main"
                  : "grey.300",
                borderRadius: 1,
                p: 1.5,
              }}
            >
              <Typography
                variant="body1"
                sx={{ fontWeight: "fontWeightBold", mb: 1 }}
              >
                {t("deltaChargeNumber", { charge: basket.basket_number })}
              </Typography>
              <Table
                size="small"
                sx={{
                  "& .MuiTableCell-root": { px: 1 },
                  "& .MuiTableRow-root:last-of-type .MuiTableCell-root": {
                    borderBottom: 0,
                  },
                }}
              >
                <TableBody>
                  {layers.map((layer) => (
                    <TableRow key={layer.layer_number}>
                      <TableCell
                        sx={{ width: "1%", pl: 0, color: "text.secondary" }}
                      >
                        {layer.layer_number}
                      </TableCell>
                      <TableCell>
                        {shortNameByMaterialName.get(layer.material_name) ??
                          layer.material_name}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ pr: 0, whiteSpace: "nowrap" }}
                      >
                        <Typography variant="body2Mono">
                          {layer.quantity.unit === "mass"
                            ? formatWeight(layer.quantity.value)
                            : `${String(layer.quantity.value)} ${layer.quantity.unit}`}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
            <Stack sx={{ pt: 1.5 }}>
              <DeltaMaterialRows
                basket={basket}
                rows={rowsByBasketNumber.get(basket.basket_number) ?? []}
                onRowsChange={(rows) => {
                  onRowsChange({ basketNumber: basket.basket_number, rows });
                }}
                materials={materials}
              />
            </Stack>
          </Fragment>
        );
      })}
    </Box>
  );
};
